/**
 * Wire-контракт чтения чеков AQSI (`receipt-read`/`receipt-hierarchy` 1.0-draft.2,
 * C4a) и его проверка на границе. Конверт `{kind, resourceId, contractVersion,
 * schemaVersion, data}`; ответ чужого ресурса — ошибка, а не данные.
 */
import {
  array,
  decimal,
  envelopeData,
  fail,
  flag,
  flagOrNull,
  instant,
  instantOrNull,
  integer,
  integerOrNull,
  record,
  sameResource,
  text,
  textOrNull,
  resourceIdOf,
} from '../../../shared/uc1b/wire'
import type {
  JournalJob,
  MyShift,
  ReceiptDetail,
  ReceiptItem,
  ReceiptLine,
  ReceiptPage,
  ReceiptRegisters,
  ReceiptShifts,
  ReceiptSourceState,
  ReceiptSummary,
} from '../model/types'

export const RECEIPT_KINDS = {
  list: 'aqsi.receipts',
  detail: 'aqsi.receipt-detail',
  registers: 'aqsi.receipt-registers',
  shifts: 'aqsi.receipt-shifts',
  journal: 'aqsi.receipt-journal',
  myShifts: 'aqsi.my-shifts',
  summary: 'aqsi.receipt-summary',
} as const

function receiptOf(raw: unknown, resourceId: string, where: string): ReceiptItem {
  const item = record(raw, where)
  const owner = resourceIdOf(item.resourceId, `${where}.resourceId`)
  if (!sameResource(owner, resourceId)) fail(`${where}: чек принадлежит другому ресурсу`)
  return {
    id: text(item.id, `${where}.id`),
    at: instantOrNull(item.at, `${where}.at`),
    shiftNumber: integerOrNull(item.shiftNumber, `${where}.shiftNumber`),
    docNumber: integerOrNull(item.docNumber, `${where}.docNumber`),
    fiscalAttribute: text(item.fiscalAttribute, `${where}.fiscalAttribute`),
    totalKopecks: integer(item.totalKopecks, `${where}.totalKopecks`),
    cashKopecks: integer(item.cashKopecks, `${where}.cashKopecks`),
    cashlessKopecks: integer(item.cashlessKopecks, `${where}.cashlessKopecks`),
    isRefund: flag(item.isRefund, `${where}.isRefund`),
    cashierName: text(item.cashierName, `${where}.cashierName`),
    deviceSerialNumber: text(item.deviceSerialNumber, `${where}.deviceSerialNumber`),
    returnedKopecks: integer(item.returnedKopecks, `${where}.returnedKopecks`),
    saleId: textOrNull(item.saleId, `${where}.saleId`),
    resourceName: text(item.resourceName, `${where}.resourceName`),
    confirmed: flag(item.confirmed, `${where}.confirmed`),
    // Старый ответ службы ссылок на чеки возврата не знает: их просто нет.
    refunds: item.refunds === undefined || item.refunds === null ? [] : array(item.refunds, `${where}.refunds`).map((raw, index) => {
      const refund = record(raw, `${where}.refunds[${index}]`)
      return { id: text(refund.receiptId, `${where}.refunds[${index}].receiptId`), at: instant(refund.at, `${where}.refunds[${index}].at`) }
    }),
    // У чека возврата — исходный чек, если возврат пробит этой программой; старый ответ службы его не знает.
    sourceReceiptId: textOrNull(item.sourceReceiptId, `${where}.sourceReceiptId`),
  }
}

function lineOf(raw: unknown, where: string): ReceiptLine {
  const line = record(raw, where)
  return {
    ordinal: integer(line.ordinal, `${where}.ordinal`),
    name: text(line.name, `${where}.name`),
    quantity: decimal(line.quantity, `${where}.quantity`),
    priceKopecks: integer(line.priceKopecks, `${where}.priceKopecks`),
    amountKopecks: integer(line.amountKopecks, `${where}.amountKopecks`),
    vatRateCode: integer(line.vatRateCode, `${where}.vatRateCode`),
    subjectCode: integer(line.subjectCode, `${where}.subjectCode`),
    barcode: textOrNull(line.barcode, `${where}.barcode`),
    returnedQuantity: decimal(line.returnedQuantity, `${where}.returnedQuantity`),
    unitCode: integer(line.unitCode, `${where}.unitCode`),
    externalId: textOrNull(line.externalId, `${where}.externalId`),
  }
}

function stateOf(raw: unknown, resourceId: string): ReceiptSourceState {
  const state = record(raw, 'state')
  if (!sameResource(resourceIdOf(state.resourceId, 'state.resourceId'), resourceId)) {
    fail('state: источник принадлежит другому ресурсу')
  }
  const capabilities = state.capabilities === null || state.capabilities === undefined ? null : record(state.capabilities, 'state.capabilities')
  return {
    available: flag(state.available, 'state.available'),
    message: text(state.message, 'state.message'),
    syncedAt: instantOrNull(state.syncedAt, 'state.syncedAt'),
    count: integer(state.count, 'state.count'),
    isFiscal: flag(state.isFiscal, 'state.isFiscal'),
    kind: text(state.kind, 'state.kind'),
    warning: textOrNull(state.warning, 'state.warning'),
    title: text(state.title, 'state.title'),
    refundsKnown: capabilities !== null && flagOrNull(capabilities.refunds, 'state.capabilities.refunds') === true,
    // Служба до RP-FB41 этих полей не присылала: выдача тогда из журнала программы и полная.
    origin: state.origin === undefined ? null : textOrNull(state.origin, 'state.origin'),
    readAt: state.readAt === undefined ? null : instantOrNull(state.readAt, 'state.readAt'),
    complete: state.complete === undefined ? true : flag(state.complete, 'state.complete'),
  }
}

export function decodeReceiptPage(raw: unknown, resourceId: string): ReceiptPage {
  const data = record(envelopeData(raw, RECEIPT_KINDS.list, resourceId), 'data')
  const page = record(data.page, 'data.page')
  return {
    items: array(page.items, 'page.items').map((item, index) => receiptOf(item, resourceId, `page.items[${index}]`)),
    nextCursor: textOrNull(page.nextCursor, 'page.nextCursor'),
    snapshotId: text(page.snapshotId, 'page.snapshotId'),
    complete: flag(page.complete, 'page.complete'),
    observedAt: instant(page.observedAt, 'page.observedAt'),
    state: stateOf(data.state, resourceId),
    shiftNumber: integerOrNull(data.shiftNumber, 'data.shiftNumber'),
    totalCount: integer(data.totalCount, 'data.totalCount'),
  }
}

/** Детализация: номер в адресе и `receipt.id` обязаны совпасть (проверка договора). */
export function decodeReceiptDetail(raw: unknown, resourceId: string, receiptId: string): ReceiptDetail {
  const data = record(envelopeData(raw, RECEIPT_KINDS.detail, resourceId), 'data')
  const receipt = receiptOf(data.receipt, resourceId, 'receipt')
  if (receipt.id !== receiptId) fail('в ответе другой номер чека, чем в запросе')
  return {
    receipt,
    lines: array(data.lines, 'lines').map((line, index) => lineOf(line, `lines[${index}]`)),
    cardSlipId: textOrNull(data.cardSlipId, 'cardSlipId'),
    isFiscal: flag(data.isFiscal, 'isFiscal'),
    origin: data.origin === undefined ? null : textOrNull(data.origin, 'origin'),
  }
}

export function decodeReceiptRegisters(raw: unknown, resourceId: string): ReceiptRegisters {
  const data = record(envelopeData(raw, RECEIPT_KINDS.registers, resourceId), 'data')
  return {
    state: stateOf(data.state, resourceId),
    registers: array(data.registers, 'registers').map((entry, index) => {
      const register = record(entry, `registers[${index}]`)
      return {
        id: text(register.id, `registers[${index}].id`),
        title: text(register.title, `registers[${index}].title`),
        serialNumber: text(register.serialNumber, `registers[${index}].serialNumber`),
        shop: text(register.shop, `registers[${index}].shop`),
      }
    }),
  }
}

export function decodeReceiptShifts(raw: unknown, resourceId: string, registerId: string): ReceiptShifts {
  const data = record(envelopeData(raw, RECEIPT_KINDS.shifts, resourceId), 'data')
  const answered = text(data.registerId, 'registerId')
  if (answered !== registerId) fail('в ответе другая касса, чем в запросе')
  return {
    state: stateOf(data.state, resourceId),
    registerId: answered,
    shifts: array(data.shifts, 'shifts').map((entry, index) => {
      const shift = record(entry, `shifts[${index}]`)
      return {
        id: text(shift.id, `shifts[${index}].id`),
        number: integer(shift.number, `shifts[${index}].number`),
        openedAt: instantOrNull(shift.openedAt, `shifts[${index}].openedAt`),
        closedAt: instantOrNull(shift.closedAt, `shifts[${index}].closedAt`),
        isOpen: flagOrNull(shift.isOpen, `shifts[${index}].isOpen`),
        updatedAt: instantOrNull(shift.updatedAt, `shifts[${index}].updatedAt`),
        openedByCashier: shift.openedByCashier == null ? null : text(shift.openedByCashier, 'shift.openedByCashier'),
        closedByCashier: shift.closedByCashier == null ? null : text(shift.closedByCashier, 'shift.closedByCashier'),
        journal: shift.journal === undefined || shift.journal === null ? null : (() => {
          const journal = record(shift.journal, 'shift.journal')
          return { count: integer(journal.count, 'shift.journal.count'), firstReceiptAt: instantOrNull(journal.firstReceiptAt, 'shift.journal.firstReceiptAt'),
            lastReceiptAt: instantOrNull(journal.lastReceiptAt, 'shift.journal.lastReceiptAt'), saleKopecks: integer(journal.saleKopecks, 'shift.journal.saleKopecks'),
            refundKopecks: integer(journal.refundKopecks, 'shift.journal.refundKopecks'), cashiers: array(journal.cashiers, 'shift.journal.cashiers').map((name) => text(name, 'shift.journal.cashier')) }
        })(),
        cleanup: shift.cleanup === undefined || shift.cleanup === null ? null : (() => {
          const cleanup = record(shift.cleanup, 'shift.cleanup')
          const state = text(cleanup.state, 'shift.cleanup.state')
          if (state !== 'cleared' && state !== 'problem') fail(`неизвестное состояние очистки смены «${state}»`)
          return { state, reason: textOrNull(cleanup.reason, 'shift.cleanup.reason'), count: integer(cleanup.count, 'shift.cleanup.count'),
            saleKopecks: integer(cleanup.saleKopecks, 'shift.cleanup.saleKopecks'), refundKopecks: integer(cleanup.refundKopecks, 'shift.cleanup.refundKopecks'),
            at: instant(cleanup.at, 'shift.cleanup.at') }
        })(),
      }
    }),
  }
}

/** Снимок задания сверки. `data: null` — сверок ещё не было: это не «успешно». */
export function decodeJournalJob(raw: unknown, resourceId: string): JournalJob | null {
  const data = envelopeData(raw, RECEIPT_KINDS.journal, resourceId)
  if (data === null || data === undefined) return null
  const job = record(data, 'data')
  return {
    operationId: text(job.operationId, 'operationId'),
    version: integer(job.version, 'version'),
    status: text(job.status, 'status'),
    syncedAt: instantOrNull(job.syncedAt, 'syncedAt'),
    attemptedAt: instantOrNull(job.attemptedAt, 'attemptedAt'),
    error: textOrNull(job.error, 'error'),
    added: integer(job.added, 'added'),
    linked: integer(job.linked, 'linked'),
  }
}

/** Итоги по отбору (`aqsi.receipt-summary`): приход и возвраты раздельно, деньги — копейки по модулю. */
export function decodeReceiptSummary(raw: unknown, resourceId: string): ReceiptSummary {
  const data = record(envelopeData(raw, RECEIPT_KINDS.summary, resourceId), 'data')
  return {
    saleCount: integer(data.saleCount, 'saleCount'),
    saleKopecks: integer(data.saleKopecks, 'saleKopecks'),
    saleCashKopecks: integer(data.saleCashKopecks, 'saleCashKopecks'),
    saleCashlessKopecks: integer(data.saleCashlessKopecks, 'saleCashlessKopecks'),
    refundCount: integer(data.refundCount, 'refundCount'),
    refundKopecks: integer(data.refundKopecks, 'refundKopecks'),
    refundCashKopecks: integer(data.refundCashKopecks, 'refundCashKopecks'),
    refundCashlessKopecks: integer(data.refundCashlessKopecks, 'refundCashlessKopecks'),
    cashiers: array(data.cashiers, 'cashiers').map((entry, index) => {
      const cashier = record(entry, `cashiers[${index}]`)
      return { name: text(cashier.name, `cashiers[${index}].name`), count: integer(cashier.count, `cashiers[${index}].count`) }
    }),
    // Служба прежней версии списка по кассам не присылала: дерево тогда без чисел.
    registers:
      data.registers === undefined
        ? []
        : array(data.registers, 'registers').map((entry, index) => {
            const register = record(entry, `registers[${index}]`)
            return { serialNumber: text(register.serialNumber, `registers[${index}].serialNumber`), count: integer(register.count, `registers[${index}].count`) }
          }),
    // Служба прежней версии дней не присылала: списка дней тогда нет.
    days:
      data.days === undefined
        ? []
        : array(data.days, 'days').map((entry, index) => {
            const day = record(entry, `days[${index}]`)
            return {
              day: text(day.day, `days[${index}].day`),
              saleCount: integer(day.saleCount, `days[${index}].saleCount`),
              saleKopecks: integer(day.saleKopecks, `days[${index}].saleKopecks`),
              refundCount: integer(day.refundCount, `days[${index}].refundCount`),
              refundKopecks: integer(day.refundKopecks, `days[${index}].refundKopecks`),
              saleCashKopecks: integer(day.saleCashKopecks ?? 0, `days[${index}].saleCashKopecks`),
              saleCashlessKopecks: integer(day.saleCashlessKopecks ?? 0, `days[${index}].saleCashlessKopecks`),
              refundCashKopecks: integer(day.refundCashKopecks ?? 0, `days[${index}].refundCashKopecks`),
              refundCashlessKopecks: integer(day.refundCashlessKopecks ?? 0, `days[${index}].refundCashlessKopecks`),
            }
          }),
    observedAt: instant(data.observedAt, 'observedAt'),
    origin: data.origin === undefined ? null : textOrNull(data.origin, 'origin'),
    complete: data.complete === undefined ? true : flag(data.complete, 'complete'),
    warning: data.warning === undefined ? null : textOrNull(data.warning, 'warning'),
    readAt: data.readAt === undefined ? null : instantOrNull(data.readAt, 'readAt'),
  }
}

/** Мои смены за период (`aqsi.my-shifts`): касса, номер, начало и конец (`null` — открыта). */
export function decodeMyShifts(raw: unknown, resourceId: string): readonly MyShift[] {
  const data = envelopeData(raw, RECEIPT_KINDS.myShifts, resourceId)
  return array(data, 'data').map((entry, index) => {
    const shift = record(entry, `data[${index}]`)
    return {
      deviceId: integer(shift.deviceId, `data[${index}].deviceId`),
      deviceSerialNumber: text(shift.deviceSerialNumber, `data[${index}].deviceSerialNumber`),
      shiftNumber: integer(shift.shiftNumber, `data[${index}].shiftNumber`),
      startedAt: instant(shift.startedAt, `data[${index}].startedAt`),
      endedAt: instantOrNull(shift.endedAt, `data[${index}].endedAt`),
    }
  })
}
